import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core'
import { FlexLayoutModule } from '@angular/flex-layout'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'
import { PipesModule } from '@mmx/pipes'
import {
  ApiService,
  CharityCareStatus,
  CharityCareTrace,
  ConfirmService,
  DEFAULT_TIMEZONE,
  PatientCharityCareStatus,
  PermissionsService,
  STATUS_TO_TRANSLATION_KEY,
} from '@mmx/shared'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'

import { escapeTraceHtml, openJsonTraceDialog, openTraceMessageDialog } from '../json-trace-dialog'

@Component({
  selector: 'mmx-charity-care-summary',
  templateUrl: './charity-care-summary.component.html',
  imports: [
    CommonModule,
    PipesModule,
    FlexLayoutModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
  ],
  standalone: true,
})
export class CharityCareSummaryComponent implements OnDestroy {

  @Input() public timezone: string | undefined
  @Input() public patientId: string | undefined

  private _patientCharityCareStatus: PatientCharityCareStatus | undefined
  @Input()
  set patientCharityCareStatus(status: PatientCharityCareStatus | undefined) {
    this._patientCharityCareStatus = status
    this.charityCareQualificationStatus =
      this.getCharityCareQualification(status)
  }
  get patientCharityCareStatus() {
    return this._patientCharityCareStatus
  }

  readonly DEFAULT_TIMEZONE = DEFAULT_TIMEZONE
  charityCareQualificationStatus: string | undefined
  traceLoading = false
  readonly isSystemAdministrator: boolean

  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly apiService: ApiService,
    private readonly confirmService: ConfirmService,
    private readonly translate: TranslateService,
    private readonly permissionsService: PermissionsService,
    private readonly cd: ChangeDetectorRef,
  ) {
    this.isSystemAdministrator = this.permissionsService.isSystemAdministrator()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  get canShowTrace(): boolean {
    return this.isSystemAdministrator
      && !!this.patientId
      && !!this.patientCharityCareStatus?.charityCareId
  }

  getCharityCareQualification(
    patientCharityCareStatus: PatientCharityCareStatus | undefined,
  ): string {
    return STATUS_TO_TRANSLATION_KEY[patientCharityCareStatus?.status ?? CharityCareStatus.NOT_STATUSED] ?? 'charity-care-summary.not-screened'
  }

  onTrace(): void {
    if (!this.patientId || this.traceLoading) {
      return
    }

    this.traceLoading = true
    this.apiService.getCharityCareTrace(this.patientId).pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: (trace) => {
        this.traceLoading = false
        this.cd.markForCheck()
        this.displayTrace(trace)
      },
      error: (err) => {
        this.traceLoading = false
        this.cd.markForCheck()
        openTraceMessageDialog(
          this.confirmService,
          err?.error?.message ?? this.translate.instant('charity-care-summary.trace-load-error'),
          this.destroy$,
        )
      },
    })
  }

  private displayTrace(trace: CharityCareTrace): void {
    const fplLine = trace.federalPovertyLevel != null
      ? `<strong>${escapeTraceHtml(this.translate.instant('charity-care-summary.fpl-percentage'))}:</strong> ${trace.federalPovertyLevel}<br>`
      : ''
    openJsonTraceDialog(
      this.confirmService,
      this.translate.instant('charity-care-summary.trace'),
      trace,
      fplLine,
      this.destroy$,
    )
  }
}

